export async function handleApiResponse<T>(
  response: Response,
  defaultErrorMessage = 'An error occurred',
): Promise<T> {
  if (!response.ok) {
    return handleApiError(response, defaultErrorMessage);
  }

  if (response.status === 204) {
    return null as T; // No content
  }

  const textResponse = await response.text();
  return parseResponse<T>(textResponse);
}

function parseResponse<T>(textResponse: string): T {
  if (textResponse.trim() === '') {
    return null as T; // No content
  }

  try {
    return JSON.parse(textResponse) as T;
  } catch (error) {
    console.error('Failed to parse JSON:', error);
    return textResponse as unknown as T; // Fallback to raw text
  }
}

async function handleApiError(
  response: Response,
  defaultErrorMessage: string,
): Promise<never> {
  const textResponse = await response.text().catch(() => '');

  try {
    throw { ...JSON.parse(textResponse), name: 'ApiError' };
  } catch (error) {
    if (isApiError(error)) {
      throw error;
    }
  }

  throw new Error(
    `${defaultErrorMessage}: ${response.status} ${response.statusText} - ${textResponse}`,
  );
}

function isApiError(error: unknown): error is ApiError {
  return (
    typeof error === 'object' &&
    error !== null &&
    (error as ApiError)?.name === 'ApiError'
  );
}

interface ApiError {
  readonly name: string;
  readonly [key: string]: unknown;
}
